import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { subscribe } from "../lib/api";
import { submitToNetlify } from "../lib/netlify";
import { subscribeToMailchimp } from "../lib/mailchimp";
import { toast } from "sonner";

const STORAGE_KEY = "salumi_newsletter_shown_v1";

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), 14000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, "1");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email");
    setLoading(true);

    const [mailchimpResult, netlifyResult, backendResult] = await Promise.allSettled([
      subscribeToMailchimp(email),
      submitToNetlify("newsletter-popup", { email, source: "popup" }),
      subscribe(email, "popup"),
    ]);
    const mailchimpOk = mailchimpResult.status === "fulfilled" && mailchimpResult.value === true;
    const netlifyOk = netlifyResult.status === "fulfilled" && netlifyResult.value === true;
    const backendOk = backendResult.status === "fulfilled";

    if (mailchimpOk || netlifyOk || backendOk) {
      toast.success("Welcome to the table.");
      dismiss();
    } else {
      toast.error("Couldn't reach us. Email salumitucson@gmail.com.");
    }
    setLoading(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-6"
          data-testid="newsletter-popup"
        >
          <div
            className="absolute inset-0 bg-espresso/70 backdrop-blur-md"
            onClick={dismiss}
            aria-hidden="true"
          />
          <motion.div
            initial={{ scale: 0.92, y: 30, opacity: 0, filter: "blur(8px)" }}
            animate={{ scale: 1, y: 0, opacity: 1, filter: "blur(0px)" }}
            exit={{ scale: 0.92, y: 30, opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-xl bg-ivory text-espresso p-10 md:p-14 shadow-2xl"
          >
            <button
              onClick={dismiss}
              data-testid="newsletter-close"
              aria-label="Close"
              className="absolute top-4 right-4 p-2 text-espresso/50 hover:text-espresso"
            >
              <X strokeWidth={1.25} className="size-5" />
            </button>
            <p className="text-[10px] uppercase tracking-[0.3em] text-olive mb-4">An invitation</p>
            <h3 className="font-serif text-4xl md:text-5xl leading-[0.95] tracking-editorial mb-5">
              Join the Salumi <em className="not-italic text-terracotta">guest list</em>.
            </h3>
            <p className="text-sm text-espresso/70 mb-8 leading-relaxed">
              Seasonal announcements, early booking access, and a quiet note when something new lands at the table.
            </p>
              <form
                onSubmit={onSubmit}
                className="space-y-4"
                data-testid="newsletter-popup-form"
                name="newsletter-popup"
                data-netlify="true"
                netlify-honeypot="bot-field"
                method="POST"
              >
                <input type="hidden" name="form-name" value="newsletter-popup" />
                <input type="hidden" name="bot-field" />
              <input
                type="email"
                name="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-0 border-b border-espresso/30 text-xl py-3 placeholder-espresso/30 focus:outline-none focus:border-espresso"
                required
                data-testid="popup-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="popup-subscribe-button"
                className="w-full bg-espresso text-ivory py-4 text-[11px] uppercase tracking-[0.22em] hover:bg-terracotta transition-colors disabled:opacity-50"
              >
                {loading ? "Sending…" : "Reserve my seat"}
              </button>
            </form>
            <button onClick={dismiss} className="mt-6 text-xs text-espresso/40 hover:text-espresso underline-offset-4 hover:underline" data-testid="newsletter-dismiss">
              Not right now
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
